import type { Palette, ReviewItem } from './types';

type ReviewsProps = { palette: Palette; reviews: ReviewItem[] };

export function ReviewsSection({ palette, reviews }: ReviewsProps) {
  return (
    <section 
      id="reviews" 
      className="relative px-6 py-16 scroll-mt-24 md:scroll-mt-28 lg:px-12"
      style={{ backgroundColor: '#FFFFFF' }}
    >
      {/* Decorative Background Blob */}
      <div 
        className="absolute right-0 top-1/2 h-[600px] w-[600px] -translate-y-1/2 translate-x-1/3 rounded-full opacity-30 blur-[120px]"
        style={{ background: `${palette.sage}40` }}
      />

      <div className="relative mx-auto max-w-7xl">
        
        {/* HEADER: Centered & Confident */}
        <div className="mx-auto max-w-3xl text-center mb-12">
          <div className="inline-flex items-center gap-2 rounded-full border border-slate-100 bg-slate-50 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-500">
             <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: palette.clay }} />
             Community Trust
          </div>
          
          <h2 className="mt-4 text-3xl font-bold leading-tight text-slate-900 sm:text-4xl">
            What your neighbors are saying.
          </h2>
          <p className="mt-4 text-base text-slate-600">
            Real feedback from Texas homeowners who stopped stressing and started enjoying their upgrades.
          </p>
        </div>

        {/* REVIEWS GRID */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {reviews.map((review, idx) => (
            <div 
              key={idx} 
              className="group relative flex h-full flex-col justify-between rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-emerald-900/5"
            >
              {/* Decorative Big Quote Mark */}
              <div className="absolute top-6 right-6 text-6xl font-serif text-slate-100 select-none group-hover:text-emerald-50 transition-colors">
                ”
              </div>

              <div>
                {/* 5 Stars */}
                <div className="flex gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className="w-4 h-4 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>

                <p className="relative z-10 text-base font-medium leading-relaxed text-slate-700">
                  "{review.quote}"
                </p>
              </div>

              <div className="mt-6 flex items-center justify-between border-t border-slate-50 pt-4">
                <div>
                  <div className="text-sm font-bold text-slate-900">{review.name}</div>
                  <div className="text-xs text-slate-400">Verified Homeowner</div>
                </div>
                
                {/* Service Tag */}
                <span 
                  className="rounded-lg px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider"
                  style={{ backgroundColor: `${palette.forest}10`, color: palette.forest }}
                >
                  {review.tag}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
